/**
 * backgroundAnalysis.ts — состояние фонового ПЛАТНОГО разбора приложения
 * (WEB-1362, решение владельца 02.09, п.2 «отказ должен быть виден»).
 *
 * ЗАЧЕМ. Разбор ссылки на приложение структурно требует уже созданного
 * проекта, поэтому он уходит фоном ПОСЛЕ закрытия мастера создания
 * (`CreateProjectWizard`, WEB-617). Раньше его результат глотался целиком —
 * `contextApi.analyze(...).catch(() => undefined)`, — и человек, у которого не
 * хватило кредитов или не было тарифа Pro, видел вечно пустую вкладку
 * «Контекст» без единого слова о причине. Это дефект сам по себе,
 * независимо от того, предупредили мы о цене или нет.
 *
 * ЧТО ЭТО. Крошечная внешняя память «проект → что с его фоновым разбором»,
 * на которую вкладка «Контекст» подписывается через `useSyncExternalStore`.
 * Redux сюда не заводится намеренно: состояние живёт ровно столько, сколько
 * открыта вкладка браузера, никем, кроме этой вкладки, не читается и не
 * попадает ни в одно сохранение.
 *
 * ПРИЧИНА ОТКАЗА — ЧЕРЕЗ ОБЩИЙ ПЕРЕВОДЧИК. Текст берётся из отказа сервера
 * `localizeError`-ом, тем же путём, что у соседей (`pages/AcceptInvite.tsx`,
 * `utils/bulkDelete.ts`, `utils/refundError.ts`): знакомый код («не хватает
 * средств», «нужен Pro») превращается в русскую причину, незнакомый
 * английский текст наружу не просачивается.
 *
 * ГРАНИЦА, названная честно: после перезагрузки страницы память пуста —
 * серверного следа у фонового разбора сегодня нет, а заводить новую ручку
 * карточка прямо запрещает. Тогда вкладка показывает обычное «профиля пока
 * нет», как и до правки.
 */
import { useSyncExternalStore } from 'react';

import { localizeError } from '@/utils/localizeError';
import { extractApiError } from '@/api/errorStatus';
import { pickStrings } from '@/i18n';
import { settingsStrings } from '@/i18n/dictionaries/settings';

export type BackgroundAnalysisState = 'running' | 'done' | 'failed';

export interface BackgroundAnalysisStatus {
  state: BackgroundAnalysisState;
  /** Причина отказа на русском (только для state === 'failed'). */
  message?: string;
  /**
   * WEB-3022: машинный код отказа сервера (только для state === 'failed',
   * если сервер его прислал). По нему вкладка решает, ставить ли рядом с
   * причиной действие — например «Пополнить баланс» при INSUFFICIENT_BALANCE.
   */
  code?: string;
  /**
   * WEB-3990: сколько кредитов списал разбор (только для state === 'done',
   * если сервер прислал целое ≥ 0). Плашка «готово» на главной пишет
   * «Списано N кр.». Старый сервер поля не шлёт — поля нет.
   */
  chargedCredits?: number;
}

/**
 * WEB-3990: сумма списанного из ответа axios (`{ data: { chargedCredits } }`,
 * мастер передаёт его как есть). Только целое ≥ 0; иначе — нет суммы.
 */
function chargedCreditsOf(value: unknown): number | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const data = (value as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return undefined;
  const n = (data as { chargedCredits?: unknown }).chargedCredits;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : undefined;
}

/**
 * WEB-1874: фолбэк-причина отказа переехала в словарь кабинета и читается на
 * языке ЧЕЛОВЕКА в момент показа, а не в момент загрузки модуля.
 */
function analyzeFailedFallback(): string {
  return pickStrings(settingsStrings).ctxAnalyzeFailedFallback;
}

/**
 * СРОК ГОДНОСТИ ОТМЕТКИ «ВЫПОЛНЯЕТСЯ» (ревизия пачки 9, Ф12).
 *
 * Отметку снимал только исход запроса, а он мог не наступить никогда: вкладку
 * мастера закрыли на полпути, сеть повисла, ответ потерялся. Отметка при этом
 * оставалась навсегда, и вкладка «Контекст» до конца жизни страницы обещала
 * человеку идущий разбор, которого нет.
 *
 * Десять минут — с большим запасом больше любого честного разбора (он живёт
 * секунды-минуты) и заметно меньше того времени, за которое «выполняется»
 * превращается в издевательство. Вышел срок — отметка снимается, и вкладка
 * показывает ровно то же, что после перезагрузки страницы: обычное «профиля
 * пока нет» (граница, названная в шапке модуля). Честное «не знаю» лучше
 * уверенного вранья про идущую работу.
 */
export const BACKGROUND_ANALYSIS_TTL_MS = 10 * 60 * 1000;

const byProject = new Map<string, BackgroundAnalysisStatus>();
const listeners = new Set<() => void>();
/**
 * Когда отметка поставлена. ОТДЕЛЬНОЙ памятью, а не полем статуса: статус
 * уезжает в компонент и сравнивается по ссылке (`useSyncExternalStore`), и
 * служебное время внутри него было бы видно наружу без всякой нужды.
 */
const startedAt = new Map<string, number>();
/** Будильники срока годности — по одному на проект. */
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function notify(): void {
  // Копия набора: подписчик вправе отписаться прямо в обработчике.
  for (const listener of Array.from(listeners)) listener();
}

function stopExpiry(projectId: string): void {
  const timer = expiryTimers.get(projectId);
  if (timer === undefined) return;
  clearTimeout(timer);
  expiryTimers.delete(projectId);
}

function forget(projectId: string): boolean {
  stopExpiry(projectId);
  startedAt.delete(projectId);
  return byProject.delete(projectId);
}

function set(projectId: string, status: BackgroundAnalysisStatus): void {
  byProject.set(projectId, status);
  notify();
}

/**
 * Запустить фоновый разбор и запомнить его исход.
 *
 * @param projectId проект, к вкладке «Контекст» которого относится разбор.
 * @param run       сам вызов (мастер передаёт `contextApi.analyze`).
 */
export function startBackgroundAnalysis(
  projectId: string,
  run: () => Promise<unknown>,
): void {
  // Повторный запуск того же проекта заводит срок годности заново.
  stopExpiry(projectId);
  startedAt.set(projectId, Date.now());
  set(projectId, { state: 'running' });
  expiryTimers.set(
    projectId,
    setTimeout(() => {
      expiryTimers.delete(projectId);
      // Разбор мог успеть закончиться — тогда снимать нечего.
      if (byProject.get(projectId)?.state !== 'running') return;
      forget(projectId);
      notify();
    }, BACKGROUND_ANALYSIS_TTL_MS),
  );
  void run().then(
    (value) => {
      stopExpiry(projectId);
      startedAt.delete(projectId);
      const chargedCredits = chargedCreditsOf(value);
      set(projectId, {
        state: 'done',
        ...(chargedCredits !== undefined ? { chargedCredits } : {}),
      });
    },
    (err: unknown) => {
      stopExpiry(projectId);
      startedAt.delete(projectId);
      const { code } = extractApiError(err);
      set(projectId, {
        state: 'failed',
        message: localizeError(err, analyzeFailedFallback()),
        ...(code ? { code } : {}),
      });
    },
  );
}

/** Код отказа «не хватает кредитов» — тот же, что присылает сервер. */
const NO_BALANCE_CODE = 'INSUFFICIENT_BALANCE';

/**
 * WEB-3760 юнит 2б (решение владельца 28.10): мастер нового проекта увидел, что
 * кредитов меньше цены разбора, и платный разбор НЕ запускал. Проекту ставится
 * та же отметка, что поставил бы отказ сервера 402 `INSUFFICIENT_BALANCE`, —
 * «Главная» нового проекта покажет «Разбор не запускали: не хватило кредитов»,
 * вкладка «Контекст» — причину и «Пополнить». Причину текстом не храним: её
 * по коду даёт реестр (`localizeError`). Срок годности у отметки не нужен — он
 * только у «идёт».
 */
export function markBackgroundAnalysisNoBalance(projectId: string): void {
  stopExpiry(projectId);
  startedAt.delete(projectId);
  set(projectId, { state: 'failed', code: NO_BALANCE_CODE });
}

/**
 * Текущее состояние разбора проекта. Ссылка на объект стабильна, пока
 * состояние не менялось, — этого требует `useSyncExternalStore`.
 */
export function getBackgroundAnalysis(
  projectId: string,
): BackgroundAnalysisStatus | undefined {
  const status = byProject.get(projectId);
  if (status?.state !== 'running') return status;
  // Второй сторож срока годности, кроме будильника: усыплённой вкладке
  // браузер душит таймеры, а часы там идут — протухшую отметку не показываем
  // и тогда, когда будильник ещё не прозвонил. Память здесь НЕ трогаем: это
  // чтение снимка для `useSyncExternalStore`, а он не терпит побочных правок
  // состояния во время отрисовки. Уборку делает будильник.
  const since = startedAt.get(projectId);
  if (since !== undefined && Date.now() - since >= BACKGROUND_ANALYSIS_TTL_MS) {
    return undefined;
  }
  return status;
}

/** Забыть состояние (вкладка показала его и перечитала профиль). */
export function clearBackgroundAnalysis(projectId: string): void {
  if (!forget(projectId)) return;
  notify();
}

/** Подписка на любые изменения. Возвращает функцию отписки. */
export function subscribeBackgroundAnalysis(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Состояние фонового разбора проекта для компонента. Снимок стабилен по
 * ссылке, пока состояние не менялось, — иначе `useSyncExternalStore`
 * зациклил бы перерисовку.
 */
export function useBackgroundAnalysis(
  projectId: string | undefined,
): BackgroundAnalysisStatus | undefined {
  return useSyncExternalStore(
    subscribeBackgroundAnalysis,
    () => (projectId ? getBackgroundAnalysis(projectId) : undefined),
    () => undefined,
  );
}
